<script setup>
// Astra DS · MiniWave — la onda mínima.
//
// Cuatro barras de dos píxeles que dicen una sola cosa: **esto es lo que está
// sonando ahora**. Va donde no cabe nada más, dentro de la celda en antena de
// la parrilla y en la tarjeta del programa en emisión.
//
// No es un `Transport` encogido. Aquel pinta 56 bandas porque es la onda del
// reproductor y ahí la forma del sonido ES el contenido; aquí la forma no
// importa, importa que se mueva con lo que suena. De 56 bandas a 4 barras hay
// una diferencia de propósito, no de tamaño, y por eso son dos componentes.
//
// El motor es el mismo y no se duplica: `useAudioLevels` saca las amplitudes
// del audio real y este componente solo las agrupa y las pinta. Quien lo monta
// le pasa los niveles que ya tiene la vista.
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { crearDinamica } from "@/utils/dinamicaDeBanda";

const props = defineProps({
  // Las amplitudes del analizador, 0..1. Vienen de `useAudioLevels`.
  levels: { type: Array, default: () => [] },

  playing: { type: Boolean, default: false },
});

const BARRAS = 4;

// ── Tres estados, los mismos que `Transport` y por el mismo motivo ──
//
//   sonando con señal   las barras siguen al audio
//   sonando sin señal   forma de reposo, quieta: es el búfer de HLS, que tarda
//                       unos diez segundos, y aplanarse ahí se lee como avería
//                       justo cuando acaban de pedir la emisión
//   parado              barras bajas: no hay nada que medir, y un medidor sin
//                       señal no marca alto
//
// Las dos formas van escalonadas y no iguales: cuatro barras a la misma altura
// parecen un icono roto, no una onda.
const REPOSO = [0.4, 0.75, 0.5, 0.85];
const PARADO = [0.18, 0.3, 0.22, 0.26];

// Hay señal cuando alguna banda se despega del cero. Mismo umbral que
// `Transport`, para que las dos ondas de la casa cambien de estado a la vez.
const haySenal = computed(() =>
  props.levels.some((v) => typeof v === "number" && v > 0.01),
);

// Quien pidió no ver animaciones ve la forma de reposo y punto. Quitar solo la
// transición no bastaba: las barras seguirían saltando de altura en cada
// fotograma, que es exactamente lo que se ha pedido no ver. Se congela aquí, en
// el dato, no en el CSS.
const quieta = ref(false);
let consulta = null;
const alCambiar = (e) => { quieta.value = e.matches; };

onMounted(() => {
  if (!window.matchMedia) return;
  consulta = window.matchMedia("(prefers-reduced-motion: reduce)");
  quieta.value = consulta.matches;
  consulta.addEventListener("change", alCambiar);
});

onUnmounted(() => consulta?.removeEventListener("change", alCambiar));

// De 56 bandas a 4 barras: cada una es la MEDIA de su tramo, no el máximo.
//
// Medido con la emisión real sonando (28.08.26), reconstruyendo los niveles
// desde las 56 barras de `Transport`: la media recorre de 0,30 a 0,88 y el
// máximo se queda entre 0,49 y 1, tocando el techo casi cada muestra. Con
// catorce bandas por tramo, el máximo casi siempre encuentra alguna alta, así
// que satura y las cuatro barras se quedan arriba, todas parecidas.
function medias(l) {
  const ancho = Math.floor(l.length / BARRAS) || 1;
  const salida = [];

  for (let i = 0; i < BARRAS; i += 1) {
    let suma = 0;
    let n = 0;
    for (let j = i * ancho; j < (i + 1) * ancho && j < l.length; j += 1) {
      suma += Number(l[j]) || 0;
      n += 1;
    }
    salida.push(n ? suma / n : 0);
  }

  return salida;
}

// 🔴 Cada barra se mide contra sí misma, o la onda no se mueve: el porqué está
// en `dinamicaDeBanda`, que es el mismo motor que usa `Transport`.
const dinamica = crearDinamica(BARRAS);

const alturas = ref(PARADO);

function pintar() {
  if (quieta.value || !props.playing) {
    alturas.value = props.playing ? REPOSO : PARADO;
    return;
  }
  if (!haySenal.value) {
    alturas.value = REPOSO;
    return;
  }

  alturas.value = dinamica.normaliza(medias(props.levels));
}

watch(() => props.levels, pintar, { immediate: true });
watch([() => props.playing, quieta], pintar);
</script>

<template>
  <!-- Decorativa: lo que dice ya lo dice el texto de al lado («En emisión»,
       el nombre del programa en la celda roja). Un lector de pantalla no gana
       nada leyendo cuatro barras. -->
  <span class="mini-wave" aria-hidden="true">
    <span
      v-for="(alto, i) in alturas"
      :key="i"
      class="mini-wave__bar"
      :style="{ transform: `scaleY(${alto})` }"
    />
  </span>
</template>

<style scoped lang="scss">
.mini-wave {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 11px;
  flex: none;
  // Sin `vertical-align` la onda se sienta en la línea base y sobresale por
  // debajo del texto que acompaña.
  vertical-align: -1px;
}

.mini-wave__bar {
  width: 2px;
  height: 100%;
  // Se escala desde abajo: una barra de vúmetro crece, no se estira desde el
  // centro.
  transform-origin: bottom;
  background: currentColor;
  // El color lo pone quien la monta: en la celda roja hereda el texto de la
  // celda, en la tarjeta hereda el del kicker. Así no hay un token nuevo que
  // mantener y la onda nunca desentona con lo que tiene al lado.

  // Corto: la onda ya llega suavizada del analizador (`smoothingTimeConstant`
  // 0.5) y una transición larga encima la volvería gelatina, siempre por
  // detrás de lo que se oye.
  transition: transform 0.09s linear;
}

// Sin animación tampoco hay transición que suavizar. Lo que congela las alturas
// está arriba, en el script: esto solo evita el deslizamiento del último
// fotograma pintado.
@media (prefers-reduced-motion: reduce) {
  .mini-wave__bar {
    transition: none;
  }
}
</style>
